// cartGroupItemApis.ts
import { query } from "./strapi";

// Define error type to fix TypeScript errors
interface ApiError extends Error {
  response?: {
    status?: number;
  };
  // Ensure it extends Error to have message, stack, etc.
}

// Define a more comprehensive type for the request body
interface CartItemRequestBody {
  cartCustomId: string;
  quantity?: number;
  notes?: string;
  price?: number;
  finalPrice?: number;
  destinationZoneId?: string;
  recalculateWeight?: boolean;
  recalculateFee?: boolean;
  forceUpdate?: boolean;
  quantityChanged?: boolean;
  skipSecondaryCalculation?: boolean; // Add flag for manual update
  [key: string]: any; // Allow for additional properties to support future extensions
}

// Keep track of API calls for debugging
const apiCallTimings = {};

/**
 * Add item to cart with retry and performance optimization
 */
export async function addCartItem(
  productId: string,
  quantity: number,
  cartCustomId: string,
  groupingAttributes: Array<{ name: string; value: string }> = [],
  customizableOptions: Array<any> = [],
  notes: string = "",
  destinationZoneId?: string // Add parameter for destination zone ID
) {
  const startTime = performance.now();
  const maxRetries = 2;
  let retryCount = 0;
  let lastError: ApiError | null = null;

  // Log operation start with all parameters
  console.log(`🔄 [CART] Adding item to cart:
    - productId: ${productId}
    - quantity: ${quantity}
    - cartCustomId: ${cartCustomId}
    - session ID: ${localStorage.getItem("app_session_id")}
    - cart ID: ${localStorage.getItem("cartId")}
    - destinationZoneId: ${destinationZoneId || "not specified"}
  `);

  while (retryCount <= maxRetries) {
    try {
      console.log(
        `🛒 Adding item to cart: ${productId}, qty: ${quantity}${
          retryCount > 0 ? ` (retry ${retryCount})` : ""
        }`
      );

      // Fix headers typing issue
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // Add protocol information to help server detect secure connections
      if (typeof window !== "undefined") {
        headers["x-forwarded-proto"] = window.location.protocol.replace(
          ":",
          ""
        );
      }

      // Safely add cart ID to headers if available
      const savedCartId = localStorage.getItem("cartId");
      if (savedCartId) {
        headers["x-cart-id"] = savedCartId;
        console.log(`🔍 Adding cartId to headers: ${savedCartId}`);
      } else {
        console.log(`⚠️ No cartId available in localStorage`);
      }

      // Get the session ID for logging
      const sessionId = localStorage.getItem("app_session_id");
      if (sessionId) {
        headers["x-session-id"] = sessionId;
        console.log(`🔍 Adding sessionId to headers: ${sessionId}`);
      } else {
        console.log(`⚠️ No sessionId available in localStorage`);
      }

      const requestBody = {
        productId,
        quantity,
        cartCustomId,
        groupingAttributes,
        customizableOptions,
        notes,
        destinationZoneId,
      };

      console.log(
        `🔍 Request payload for addCartItem:`,
        JSON.stringify(requestBody)
      );
      console.log(
        `🔍 Request headers for addCartItem:`,
        JSON.stringify(headers)
      );

      const response = await query(`cart-items/add`, {
        method: "POST",
        headers,
        credentials: "include", // Include cookies for sessionId
        body: JSON.stringify(requestBody),
      });

      // Always update cartId in localStorage when it's returned in the response
      if (response?.cartId) {
        console.log(
          `🔍 Updating cartId in localStorage to: ${response.cartId}`
        );
        localStorage.setItem("cartId", response.cartId);
      } else {
        console.log(`⚠️ No cartId returned in response`);
      }

      const duration = Math.round(performance.now() - startTime);
      console.log(`✅ Item added successfully (${duration}ms)`);
      console.log("[CART] Response from addCartItem:", {
        cartId: response?.cartId,
        cartVersion: response?.cartVersion,
        hasCart: !!response?.cart,
        hasAddedItem: !!response?.addedItem,
        groupsCount: response?.cart?.groups?.length || 0,
      });

      // Log the first group's data if available
      if (response?.cart?.groups && response.cart.groups.length > 0) {
        const firstGroup = response.cart.groups[0];
        console.log("[CART] First group in addCartItem response:", {
          id: firstGroup.id,
          deliveryFee: firstGroup.deliveryFee,
          deliveryFeeType: typeof firstGroup.deliveryFee,
          totalWeight: firstGroup.totalWeight,
          totalWeightType: typeof firstGroup.totalWeight,
          subTotal: firstGroup.subTotal,
          groupTotal: firstGroup.groupTotal,
        });
      }
      return response;
    } catch (error) {
      // Type assertion to fix property access errors
      lastError = error as ApiError;
      retryCount++;

      // Detailed error logging
      console.error(
        `❌ [CART] Error in addCartItem (attempt ${retryCount}):`,
        lastError.message
      );
      console.error(`❌ [CART] Error stack: ${lastError.stack}`);

      if (lastError.response?.status) {
        console.error(
          `❌ [CART] Error status code: ${lastError.response.status}`
        );
      }

      // Store retry count in a constant to avoid closure issues in setTimeout
      const currentRetry = retryCount;

      // Only retry on specific errors that might be transient
      const isNetworkError = lastError.message?.includes("network") || false;
      const isTimeoutError = lastError.message?.includes("timeout") || false;
      const isConflictError = lastError.response?.status === 409;

      if (
        currentRetry <= maxRetries &&
        (isConflictError || isNetworkError || isTimeoutError)
      ) {
        console.warn(
          `⚠️ [CART] Retry ${currentRetry}/${maxRetries} for adding item: ${
            lastError.message || "Unknown error"
          }`
        );
        // Exponential backoff (using fixed value to avoid loop closure issues)
        const backoffTime = 200 * Math.pow(2, currentRetry);
        await new Promise((resolve) => setTimeout(resolve, backoffTime));
      } else {
        console.error(
          "❌ [CART] Error adding item to cart:",
          lastError.message
        );
        throw lastError;
      }
    }
  }

  throw lastError || new Error("Failed to add item after retries");
}

/**
 * Update cart item with retry logic
 * Requires sessionId (via cookies) and cartId (via header)
 */
export async function updateCartItem(
  cartCustomId: string,
  updates: Record<string, any> = {}
) {
  const operationId = `api-${cartCustomId}-${Date.now()}`;
  const startTime = performance.now();

  console.log(`🔄 [${operationId}] START updateCartItem API call`, { updates });
  console.trace(`Call stack for ${operationId}`);

  const cartId = localStorage.getItem("cartId");
  if (!cartId) {
    console.error(
      "❌ No cartId found in localStorage, attempting to find or create cart"
    );
    try {
      // Try to find or create a cart first
      const cartResponse = await query(`cart/find-or-create`, {
        method: "POST", // Changed from GET to POST to follow REST principles
        credentials: "include", // Include cookies for sessionId
      });

      if (cartResponse?.cartId) {
        localStorage.setItem("cartId", cartResponse.cartId);
        console.log(
          "✅ CartId retrieved and saved to localStorage:",
          cartResponse.cartId
        );
      } else {
        throw new Error("Failed to find or create cart");
      }
    } catch (findError) {
      console.error("❌ Error finding or creating cart:", findError);
      throw new Error(
        "No cart ID found in localStorage and failed to create one"
      );
    }
  }

  const maxRetries = 2;
  let retryCount = 0;
  let lastError: ApiError | null = null;

  while (retryCount <= maxRetries) {
    try {
      console.log(
        `🔄 [${operationId}] Sending request, attempt ${retryCount + 1}`
      );

      // Fix headers typing issue
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // Only add cartId to headers if it's not null
      const savedCartId = localStorage.getItem("cartId");
      if (savedCartId) {
        headers["x-cart-id"] = savedCartId;
        console.log(
          `🔍 [${operationId}] Using cart ID from localStorage: ${savedCartId}`
        );
      }

      // Always request weight and fee recalculation when quantities change
      const requestBody: CartItemRequestBody = {
        cartCustomId,
        ...updates,
        recalculateWeight: true,
        recalculateFee: true,
        skipSecondaryCalculation: updates.manualUpdate === true, // Pass special flag when manualUpdate is true
      };

      // Remove the manualUpdate flag before sending to API (it's just for our client logic)
      if ("manualUpdate" in requestBody) {
        delete requestBody.manualUpdate;
      }

      // Log the exact request before sending
      console.log(`🔄 [${operationId}] Request payload:`, {
        url: `cart-items/update`,
        method: "PATCH",
        headers: { "x-cart-id": localStorage.getItem("cartId") || "none" },
        body: { cartCustomId, ...updates },
      });

      console.log(
        `🔍 [${operationId}] Request body: ${JSON.stringify(requestBody)}`
      );
      const response = await query(`cart-items/update`, {
        method: "PATCH",
        headers,
        credentials: "include", // Include cookies for sessionId
        body: JSON.stringify(requestBody),
      });

      const duration = Math.round(performance.now() - startTime);
      console.log(
        `✅ [${operationId}] COMPLETED updateCartItem (${duration}ms)`,
        {
          cartVersion: response?.cartVersion,
          responseTime: duration,
        }
      );

      console.log("[CART-ITEM-API] Response from updateCartItem:", {
        cartId: response?.cartId,
        cartVersion: response?.cartVersion,
        hasCart: !!response?.cart,
        hasUpdatedItem: !!response?.updatedItem,
        groupsCount: response?.cart?.groups?.length || 0,
      });

      // Log the first group's data if available
      if (response?.cart?.groups && response.cart.groups.length > 0) {
        const firstGroup = response.cart.groups[0];
        console.log("[CART-ITEM-API] First group in updateCartItem response:", {
          id: firstGroup.id,
          deliveryFee: firstGroup.deliveryFee,
          deliveryFeeType: typeof firstGroup.deliveryFee,
          totalWeight: firstGroup.totalWeight,
          totalWeightType: typeof firstGroup.totalWeight,
          subTotal: firstGroup.subTotal,
          groupTotal: firstGroup.groupTotal,
        });
      }

      // Log cart totals
      if (response?.cart) {
        console.log("[CART-ITEM-API] Cart totals in updateCartItem response:", {
          totalPrice: response.cart.totalPrice,
          totalDeliveryFee: response.cart.totalDeliveryFee,
          finalTotal: response.cart.finalTotal,
          discountValue: response.cart.discountValue,
        });
      }
      return response;
    } catch (error) {
      // Type assertion to fix property access errors
      lastError = error as ApiError;
      retryCount++;

      // Store retry count in a constant to avoid closure issues in setTimeout
      const currentRetry = retryCount;

      // Only retry on specific errors that might be transient
      const isNetworkError = lastError?.message?.includes("network") || false;
      const isTimeoutError = lastError?.message?.includes("timeout") || false;
      const isConflictError = lastError?.response?.status === 409;

      if (
        currentRetry <= maxRetries &&
        (isConflictError || isNetworkError || isTimeoutError)
      ) {
        console.warn(
          `⚠️ Retry ${currentRetry}/${maxRetries} for updating item: ${
            lastError?.message || "Unknown error"
          }`
        );
        // Exponential backoff (using fixed value to avoid loop closure issues)
        const backoffTime = 200 * Math.pow(2, currentRetry);
        await new Promise((resolve) => setTimeout(resolve, backoffTime));
      } else {
        console.error("❌ Error updating cart item:", lastError);
        throw lastError;
      }
    }
  }

  throw lastError || new Error("Failed to update item after retries");
}

/**
 * Remove cart item with retry logic
 * Requires sessionId (via cookies) and cartId (via header)
 */
export async function removeCartItem(cartCustomId: string) {
  const cartId = localStorage.getItem("cartId");
  if (!cartId) {
    console.error(
      "❌ No cartId found in localStorage, attempting to find or create cart"
    );
    try {
      // Try to find or create a cart first
      const cartResponse = await query(`cart/find-or-create`, {
        method: "POST", // Changed from GET to POST to follow REST principles
        credentials: "include", // Include cookies for sessionId
      });

      if (cartResponse?.cartId) {
        localStorage.setItem("cartId", cartResponse.cartId);
        console.log(
          "✅ CartId retrieved and saved to localStorage:",
          cartResponse.cartId
        );
      } else {
        throw new Error("Failed to find or create cart");
      }
    } catch (findError) {
      console.error("❌ Error finding or creating cart:", findError);
      throw new Error(
        "No cart ID found in localStorage and failed to create one"
      );
    }
  }

  const maxRetries = 2;
  let retryCount = 0;
  let lastError: ApiError | null = null;

  while (retryCount <= maxRetries) {
    try {
      console.log(
        `🗑️ Removing item: ${cartCustomId}${
          retryCount > 0 ? ` (retry ${retryCount})` : ""
        }`
      );

      // Fix headers typing issue
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // Only add cartId to headers if it's not null
      const savedCartId = localStorage.getItem("cartId");
      if (savedCartId) {
        headers["x-cart-id"] = savedCartId;
      }

      const response = await query(`cart-items/update`, {
        method: "PATCH",
        headers,
        credentials: "include", // Include cookies for sessionId
        body: JSON.stringify({
          cartCustomId,
          quantity: 0, // Setting quantity to 0 will trigger removal in the backend
        }),
      });

      console.log("✅ Item removed successfully");
      return response;
    } catch (error) {
      // Type assertion to fix property access errors
      lastError = error as ApiError;
      retryCount++;

      // Store retry count in a constant to avoid closure issues in setTimeout
      const currentRetry = retryCount;

      // Only retry on specific errors that might be transient
      const isNetworkError = lastError?.message?.includes("network") || false;
      const isTimeoutError = lastError?.message?.includes("timeout") || false;
      const isConflictError = lastError?.response?.status === 409;

      if (
        currentRetry <= maxRetries &&
        (isConflictError || isNetworkError || isTimeoutError)
      ) {
        console.warn(
          `⚠️ Retry ${currentRetry}/${maxRetries} for removing item: ${
            lastError?.message || "Unknown error"
          }`
        );
        // Exponential backoff (using fixed value to avoid loop closure issues)
        const backoffTime = 200 * Math.pow(2, currentRetry);
        await new Promise((resolve) => setTimeout(resolve, backoffTime));
      } else {
        console.error("❌ Error removing cart item:", lastError);
        throw lastError;
      }
    }
  }

  throw lastError || new Error("Failed to remove item after retries");
}
